import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { trackEvent } from "@/lib/mixpanel";
import { useQuery } from "@tanstack/react-query";
import { apiRequest, getExtraHeaders, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Confetti } from "@/components/Confetti";
import { Illustration } from "@/components/Illustration";
import { LatexText } from "@/components/LatexText";
import { Gift, Zap, Target, Clock, ArrowUp, Flame, CheckCircle2, XCircle, Lightbulb, RefreshCw, Home, ChevronDown, ChevronUp, BookOpen } from "lucide-react";

/** What /api/test/results returns, plus the milestone lottery result it now carries. */
interface TestResultsData {
  testSummary: { subject: string; topic: string; totalQuestions: number; correctAnswers: number; accuracy: number; totalTimeSpent: number; difficulty: string };
  pointsBreakdown: { basePoints: number; effectivePoints: number; fatiguePenalty: number; bonusMultiplier: number };
  levelProgress: { currentLevel: number; currentXP: number; nextLevelXP: number; totalXP: number; xpGained: number };
  subjectBreakdown: Array<{ subject: string; points: number; fatigue: number }>;
  streaks: Array<{ subject: string; topic: string; streak: number }>;
  recommendations: Array<string | { type?: string; message: string; subjects?: string[] }>;
  campaignWins?: Array<{ id: string; prizeAmount: number; merchantName?: string | null }>;
  prize?: { won: boolean; milestone?: number; prizeAmount?: number; prizeAmountJMD?: number; merchantName?: string | null };
  isReview?: boolean;
  reviewStreak?: number;
  prereq?: { subject: string; grade: number; topicKey: string; topicDisplay: string } | null;
}
interface ReviewItem { id: string; questionText: string; chosen: string; correct: string; isCorrect: boolean; explanation?: string }
interface KidExplanation { bigIdea: string; whatWentWrong: string; steps: string[]; answer: string; tryThis?: { question: string; answer: string } }

const SUBJECT_LABELS: Record<string, string> = { math: "Mathematics", mathematics: "Mathematics", english: "Language Arts", "language arts": "Language Arts", science: "Science", "social studies": "Social Studies", "social-studies": "Social Studies", "ability test": "Ability Test", "ability-test": "Ability Test" };
/** Slugs like "math" / "social-studies" as the child sees them. */
function pretty(s: string | undefined): string {
  if (!s) return "";
  const k = s.toLowerCase().trim();
  return SUBJECT_LABELS[k] ?? k.replace(/[-_]+/g, " ").replace(/\b\w/g, ch => ch.toUpperCase());
}

function ScoreRing({ pct, correct, total }: { pct: number; correct: number; total: number }) {
  const [shown, setShown] = useState(0);
  useEffect(() => { const t = setTimeout(() => setShown(pct), 80); return () => clearTimeout(t); }, [pct]);
  const r = 62, c = 2 * Math.PI * r;
  const tone = pct >= 70 ? "#059669" : pct >= 40 ? "#D97706" : "#E11D48";
  return (
    <div className="relative w-40 h-40 md:w-44 md:h-44" data-testid="score-ring" aria-label={`${correct} of ${total} correct, ${pct} percent`}>
      <svg viewBox="0 0 150 150" className="w-full h-full -rotate-90">
        <circle cx="75" cy="75" r={r} fill="none" stroke="currentColor" className="text-muted" strokeWidth="12" />
        <circle cx="75" cy="75" r={r} fill="none" stroke={tone} strokeWidth="12" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c - (shown / 100) * c} style={{ transition: "stroke-dashoffset 900ms cubic-bezier(.2,.8,.2,1)" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-3xl md:text-4xl font-bold tabular-nums" data-testid="text-score">{correct}/{total}</div>
        <div className="text-xs font-medium text-muted-foreground">{pct}% correct</div>
      </div>
    </div>
  );
}

function StatTile({ icon, label, value, tone, testId }: { icon: React.ReactNode; label: string; value: string; tone: string; testId: string }) {
  return (
    <div className={`rounded-xl border border-border p-3 md:p-4 flex items-center gap-3 ${tone}`} data-testid={testId}>
      <div className="w-10 h-10 rounded-lg bg-white/70 dark:bg-black/20 flex items-center justify-center shrink-0">{icon}</div>
      <div className="min-w-0">
        <div className="text-lg md:text-xl font-bold tabular-nums leading-tight">{value}</div>
        <div className="text-xs font-medium text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}

function ReviewRow({ item, index }: { item: ReviewItem; index: number }) {
  const [open, setOpen] = useState(!item.isCorrect);
  const [kid, setKid] = useState<KidExplanation | null>(null);
  const [busy, setBusy] = useState(false);
  const explain = async () => {
    setBusy(true);
    try {
      const r = await apiRequest("POST", `/api/questions/${item.id}/generate-explanation`, { chosenAnswer: item.chosen });
      const j = await r.json(); if (j.kid) setKid(j.kid);
    } catch {} finally { setBusy(false); }
  };
  return (
    <div className={`rounded-xl border p-3 md:p-4 ${item.isCorrect ? "border-emerald-200 bg-emerald-50/60 dark:border-emerald-900 dark:bg-emerald-950/30" : "border-rose-200 bg-rose-50/60 dark:border-rose-900 dark:bg-rose-950/30"}`} data-testid={`review-${index}`}>
      <button type="button" className="w-full flex items-start gap-3 text-left" onClick={() => setOpen(o => !o)}>
        <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 text-white text-xs font-bold ${item.isCorrect ? "bg-emerald-500" : "bg-rose-500"}`}>{index + 1}</div>
        <div className="flex-1 min-w-0 text-sm font-medium"><LatexText text={item.questionText} /></div>
        {item.isCorrect ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" /> : <XCircle className="w-5 h-5 text-rose-600 shrink-0" />}
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />}
      </button>
      {open && (
        <div className="mt-3 pl-10 space-y-2 text-sm">
          {!item.isCorrect && <p><span className="font-semibold text-rose-700 dark:text-rose-300">You chose: </span><LatexText text={item.chosen || "(no answer)"} /></p>}
          <p><span className="font-semibold text-emerald-700 dark:text-emerald-300">Answer: </span><LatexText text={item.correct} /></p>
          {item.explanation && <div className="text-muted-foreground"><LatexText text={item.explanation} /></div>}
          {!item.isCorrect && !kid && (
            <Button size="sm" variant="outline" onClick={explain} disabled={busy} data-testid={`review-explain-${index}`}>
              {busy ? <><div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-primary mr-2" />Working it out...</> : <><Lightbulb className="w-3.5 h-3.5 mr-2" />Explain it to me</>}
            </Button>
          )}
          {kid && (
            <div className="rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-950/40 px-3 py-2 space-y-2">
              {kid.bigIdea && <p className="font-semibold">{kid.bigIdea}</p>}
              {kid.whatWentWrong && <p><span className="font-semibold text-amber-700 dark:text-amber-400">What went wrong: </span>{kid.whatWentWrong}</p>}
              {kid.steps?.length > 0 && <ol className="list-decimal pl-5 space-y-1">{kid.steps.map((st, i) => <li key={i}>{st}</li>)}</ol>}
              {kid.answer && <p><span className="font-semibold text-emerald-700 dark:text-emerald-400">Answer: </span>{kid.answer}</p>}
              {kid.tryThis?.question && <p><span className="font-semibold">Try this: </span>{kid.tryThis.question}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function TestResults() {
  const [, setLocation] = useLocation();
  const { user, isAuthenticated } = useAuth();
  const [showConfetti, setShowConfetti] = useState(false);
  const data = useMemo<TestResultsData | null>(() => { try { const raw = localStorage.getItem("lastTestResults"); return raw ? JSON.parse(raw) : null; } catch { return null; } }, []);
  const review = useMemo<ReviewItem[]>(() => { try { const raw = localStorage.getItem("lastTestReview"); return raw ? JSON.parse(raw) : []; } catch { return []; } }, []);
  const lastQuizPath = useMemo(() => { try { return localStorage.getItem("lastQuizPath") || ""; } catch { return ""; } }, []);
  // A win waiting to be claimed (this quiz's, or an older one never claimed).
  const { data: open } = useQuery<{ eligible?: boolean; prizeAmountJMD?: number; merchantName?: string | null; milestone?: number }>({
    queryKey: ["/api/prizes/check"], enabled: isAuthenticated,
    queryFn: async () => (await fetch("/api/prizes/check", { credentials: "include", headers: getExtraHeaders() })).json(),
  });
  const won = data?.prize?.won ? { amount: data.prize.prizeAmountJMD, merchant: data.prize.merchantName, milestone: data.prize.milestone, fresh: true }
    : open?.eligible ? { amount: open.prizeAmountJMD, merchant: open.merchantName, milestone: open.milestone, fresh: false } : null;

  const pct = data ? data.testSummary.accuracy : 0;
  const perfect = !!data && data.testSummary.correctAnswers === data.testSummary.totalQuestions && data.testSummary.totalQuestions > 0;
  const great = pct >= 70;
  useEffect(() => { if (great || won) { setShowConfetti(true); const t = setTimeout(() => setShowConfetti(false), 4000); return () => clearTimeout(t); } }, [great, !!won]);
  useEffect(() => { if (data?.isReview) { try { trackEvent('Review Test Completed', { subject: data.testSummary?.subject, topic: data.testSummary?.topic, reviewStreak: data.reviewStreak ?? 0 }); } catch {} } }, [data?.isReview]);

  if (!data) {
    return (
      <div className="container mx-auto px-4 py-16 max-w-xl text-center space-y-4">
        <Illustration name="owl-search" size={120} />
        <h2 className="text-xl font-bold">No results to show</h2>
        <p className="text-muted-foreground">Finish a quiz and your results will appear here.</p>
        <Button variant="outline" onClick={() => setLocation("/learning")} data-testid="button-back">Choose a subject</Button>
      </div>
    );
  }
  const { testSummary: t, levelProgress: lp, pointsBreakdown: pb, streaks = [], recommendations = [] } = data;
  const rev = !!data.isReview;
  const headline = perfect ? "Perfect score!" : great ? "Great job!" : pct >= 40 ? "Nice try!" : "Keep going!";
  const line = perfect ? "Every single one right. Owlbert is doing a happy dance." : great ? `You really know ${pretty(t.topic)}. One more round to make it stick?` : pct >= 40 ? "Good effort. Look at the ones you missed below, then go again." : "Every try makes you stronger. Check the ones you missed and try again.";
  const mins = Math.floor(t.totalTimeSpent / 60), secs = t.totalTimeSpent % 60;
  const levelPct = lp.nextLevelXP > 0 ? Math.min(100, Math.round((lp.currentXP / lp.nextLevelXP) * 100)) : 0;
  const missed = review.filter(r => !r.isCorrect).length;

  return (
    <div className="min-h-screen bg-background">
      <Confetti active={showConfetti} particleCount={60} />
      <div className="container mx-auto px-4 md:px-8 max-w-5xl py-6 md:py-8 space-y-6">

        {rev && (
          <div className="flex items-center gap-2 rounded-xl bg-amber-100 dark:bg-amber-950/40 text-amber-900 dark:text-amber-200 px-4 py-3 text-sm font-medium" data-testid="banner-review-results">
            <span className="text-base">📖</span>
            <span>Review session. This was revision of a lower grade, so it did not change your points, level, streak or leaderboard.{data.reviewStreak && data.reviewStreak >= 2 ? ` You are on a ${data.reviewStreak}-day revision streak, keep it going!` : ""}</span>
          </div>
        )}

        {!rev && won && (
          <Card className="border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30" data-testid="card-prize-won">
            <CardContent className="p-4 md:p-6 flex flex-col md:flex-row md:items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0 rotate-[-4deg]"><Gift className="w-7 h-7" /></div>
              <div className="flex-1 min-w-0">
                <h2 className="text-xl md:text-2xl font-bold">{won.fresh ? "You just won a gift card!" : "You have a gift card waiting"}</h2>
                <p className="text-sm md:text-base text-muted-foreground">
                  A <span className="font-semibold text-foreground">J${(won.amount ?? 0).toLocaleString()} {won.merchant || "Giftme"}</span> card{won.milestone ? ` for finishing ${won.milestone} tests` : ""}. Claim it and it is sent by text to a parent's phone.
                </p>
              </div>
              <Button size="lg" className="shrink-0" onClick={() => { apiRequest("POST", "/api/notifications/mark-prize-read").catch(() => {}); queryClient.invalidateQueries({ queryKey: ["/api/notifications"] }); setLocation("/claim-prize"); }} data-testid="button-claim-prize">
                <Gift className="w-4 h-4 mr-2" />Claim my prize
              </Button>
            </CardContent>
          </Card>
        )}

        <Card data-testid="card-results-hero">
          <CardContent className="p-5 md:p-8 flex flex-col md:flex-row items-center gap-6">
            <Illustration name={great ? "owl-cheer" : pct >= 40 ? "owl-wave" : "owl-book"} size={130} />
            <div className="flex-1 text-center md:text-left space-y-2">
              <div className="flex flex-wrap justify-center md:justify-start gap-2">
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-primary/10 text-primary">{pretty(t.subject)}</span>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-muted text-muted-foreground">{pretty(t.topic)}</span>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-muted text-muted-foreground capitalize">{t.difficulty}</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight" data-testid="title-test-results">{headline}</h1>
              <p className="text-muted-foreground">{line}</p>
            </div>
            <ScoreRing pct={pct} correct={t.correctAnswers} total={t.totalQuestions} />
          </CardContent>
        </Card>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatTile icon={<Zap className="w-5 h-5 text-amber-600" />} label={rev ? "Review" : (pb.bonusMultiplier !== 1 ? `XP earned (x${pb.bonusMultiplier})` : "XP earned")} value={rev ? "No points" : `+${lp.xpGained}`} tone="bg-amber-500/10" testId="card-xp" />
          <StatTile icon={<Target className="w-5 h-5 text-blue-600" />} label="Accuracy" value={`${pct}%`} tone="bg-blue-500/10" testId="card-score" />
          <StatTile icon={<Clock className="w-5 h-5 text-emerald-600" />} label="Time" value={mins > 0 ? `${mins}m ${secs}s` : `${secs}s`} tone="bg-emerald-500/10" testId="card-time" />
          <StatTile icon={<ArrowUp className="w-5 h-5 text-violet-600" />} label={`${lp.nextLevelXP - lp.currentXP} XP to level ${lp.currentLevel + 1}`} value={`Level ${lp.currentLevel}`} tone="bg-violet-500/10" testId="card-level-progress" />
        </div>
        <div className="h-2 rounded-full bg-muted overflow-hidden" aria-hidden><div className="h-full bg-primary rounded-full" style={{ width: `${levelPct}%`, transition: "width 900ms" }} /></div>

        {!rev && data.prereq && (
          <Card className="border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30" data-testid="card-prereq-nudge">
            <CardContent className="p-4 md:p-5 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0"><BookOpen className="w-6 h-6" /></div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold">Want to build a stronger base?</h3>
                <p className="text-sm text-muted-foreground">Revise <span className="font-semibold text-foreground">{data.prereq.topicDisplay}</span> from Grade {data.prereq.grade}. It is the foundation for this topic, and revising earns no points so there is no pressure.</p>
              </div>
              <Button className="shrink-0" onClick={() => setLocation(`/learning?subject=${encodeURIComponent(data.prereq!.subject)}&topic=${encodeURIComponent(data.prereq!.topicDisplay)}&reviewGrade=${data.prereq!.grade}&mode=quiz&src=nudge`)} data-testid="button-revise-prereq">
                <BookOpen className="w-4 h-4 mr-2" />Revise the basics
              </Button>
            </CardContent>
          </Card>
        )}

        {streaks.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="card-streaks">
            {streaks.slice(0, 3).map((s, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-full bg-orange-500/10 text-orange-700 dark:text-orange-300"><Flame className="w-4 h-4" />{s.streak} in a row in {s.topic}</span>
            ))}
          </div>
        )}

        {review.length > 0 && (
          <Card data-testid="card-review">
            <CardContent className="p-4 md:p-6 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-lg md:text-xl font-bold">Your answers</h2>
                <span className="text-sm text-muted-foreground">{missed === 0 ? "Nothing missed" : `${missed} to look at`}</span>
              </div>
              <div className="space-y-2">
                {review.map((item, i) => <ReviewRow key={item.id} item={item} index={i} />)}
              </div>
            </CardContent>
          </Card>
        )}

        {recommendations.length > 0 && (
          <Card data-testid="card-recommendations">
            <CardContent className="p-4 md:p-6">
              <h2 className="text-lg font-bold mb-2">What to do next</h2>
              <ul className="space-y-1.5 text-sm">
                {recommendations.map((rec, i) => <li key={i} className="flex gap-2" data-testid={`text-recommendation-${i}`}><Lightbulb className="w-4 h-4 text-primary shrink-0 mt-0.5" /><span>{typeof rec === "string" ? rec : rec?.message}</span></li>)}
              </ul>
            </CardContent>
          </Card>
        )}

        <div className="flex flex-col sm:flex-row gap-3 pb-6">
          <Button className="h-12 text-base flex-1" onClick={() => setLocation(lastQuizPath || "/learning")} data-testid="button-continue-learning">
            <RefreshCw className="w-4 h-4 mr-2" />{missed > 0 ? "Practise this topic again" : "Play again"}
          </Button>
          <Button variant="outline" className="h-12 text-base flex-1" onClick={async () => { await queryClient.invalidateQueries({ queryKey: ["/api/dashboard/stats"] }); setLocation("/dashboard"); }} data-testid="button-dashboard">
            <Home className="w-4 h-4 mr-2" />Back to dashboard
          </Button>
        </div>
      </div>
    </div>
  );
}
